import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { SEOHead } from '../components/SEOHead';

const Page    = styled.main`background:#FFFDF7; min-height:100vh; padding-top:90px;`;
const Wrap    = styled.div`max-width:760px; margin:0 auto; padding:48px 32px 100px; @media(max-width:600px){padding:32px 18px 80px;}`;
const Back    = styled(Link)`display:inline-flex; align-items:center; gap:6px; color:rgba(26,18,8,.4); font-size:.85rem; text-decoration:none; margin-bottom:36px; &:hover{color:#7C3A00;}`;
const Eyebrow = styled.p`font-family:'Raleway',sans-serif; font-size:.6rem; letter-spacing:.2em; color:#C07020; margin:0 0 8px;`;
const Title   = styled.h1`font-family:'Cormorant Garamond',serif; font-size:clamp(1.8rem,4vw,2.6rem); font-weight:400; color:#1A1208; margin:0 0 8px;`;
const Updated = styled.p`font-size:.75rem; color:rgba(26,18,8,.35); margin:0 0 48px;`;
const H2      = styled.h2`font-family:'Cormorant Garamond',serif; font-size:1.25rem; font-weight:600; color:#1A1208; margin:40px 0 10px;`;
const P       = styled.p`font-size:.93rem; color:rgba(26,18,8,.75); line-height:1.8; margin:0 0 14px;`;
const UL      = styled.ul`font-size:.93rem; color:rgba(26,18,8,.75); line-height:1.8; padding-left:22px; margin:0 0 14px;`;
const Divider = styled.hr`border:none; border-top:1px solid rgba(26,18,8,.08); margin:40px 0;`;

export default function PrivacyPage() {
  const { lang } = useLanguage();
  const { region } = useRegion();
  const isEs = lang !== 'en';

  return (
    <>
    <SEOHead
      title={isEs ? 'Aviso de Privacidad' : 'Privacy Notice'}
      description={isEs ? 'Aviso de privacidad de Hacienda Real conforme a la LFPDPPP.' : 'Privacy notice for Hacienda Real in compliance with Mexican data protection law.'}
      noindex
      lang={lang}
    />
    <Page>
      <Wrap>
        <Back to={`/${region}/cart`}><ArrowLeft size={14}/>{isEs ? 'Volver al carrito' : 'Back to cart'}</Back>
        <Eyebrow>HACIENDA REAL</Eyebrow>
        <Title>{isEs ? 'Aviso de Privacidad' : 'Privacy Notice'}</Title>
        <Updated>{isEs ? 'Última actualización: mayo 2026' : 'Last updated: May 2026'}</Updated>

        {isEs ? (
          <>
            <P>
              En cumplimiento con lo dispuesto por la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</strong> y su reglamento, <strong>Grupo Hacienda Real</strong> ("el Responsable"), con domicilio en San Juan de los Lagos, Jalisco, México, informa lo siguiente:
            </P>

            <H2>1. Datos personales que recabamos</H2>
            <UL>
              <li>Nombre completo</li>
              <li>Correo electrónico</li>
              <li>Número de teléfono</li>
              <li>Dirección de entrega (calle, número, colonia, ciudad, estado, código postal, país)</li>
            </UL>
            <P>No recabamos datos financieros directamente. Los pagos son procesados por <strong>Stripe</strong>, quien tiene sus propias políticas de privacidad.</P>

            <H2>2. Finalidad del tratamiento</H2>
            <P>Los datos personales recabados se utilizan exclusivamente para:</P>
            <UL>
              <li>Procesar y entregar tu pedido</li>
              <li>Enviarte la confirmación y actualizaciones de tu pedido por correo electrónico</li>
              <li>Atender aclaraciones o reclamaciones relacionadas con tu compra</li>
            </UL>
            <P>No utilizamos tus datos para publicidad, mercadotecnia ni los compartimos con terceros salvo las paqueterías necesarias para realizar el envío (DHL, FedEx, Estafeta, UPS u otras).</P>

            <H2>3. Transferencia de datos</H2>
            <P>Solo transferimos tus datos a:</P>
            <UL>
              <li><strong>Paqueterías</strong>: para generar la guía y entregar tu pedido</li>
              <li><strong>Stripe</strong>: para procesar el pago de forma segura</li>
            </UL>
            <P>No realizamos transferencias a terceros con fines comerciales.</P>

            <H2>4. Derechos ARCO</H2>
            <P>
              Tienes derecho a <strong>Acceder, Rectificar, Cancelar u Oponerte (ARCO)</strong> al tratamiento de tus datos personales. Para ejercer estos derechos, envía tu solicitud a:
              {' '}<a href="mailto:orders@grupohaciendareal.com">orders@grupohaciendareal.com</a>{' '}
              indicando tu nombre completo y el ejercicio que deseas realizar. Responderemos en un plazo máximo de 20 días hábiles.
            </P>

            <H2>5. Cookies y tecnologías de rastreo</H2>
            <P>Nuestro sitio web puede utilizar cookies técnicas necesarias para su funcionamiento. No utilizamos cookies de seguimiento publicitario.</P>

            <H2>6. Cambios al aviso de privacidad</H2>
            <P>Cualquier modificación a este aviso se publicará en esta misma página. Te recomendamos revisarlo periódicamente.</P>

            <H2>7. Contacto</H2>
            <P>Para cualquier duda o aclaración sobre el tratamiento de tus datos escríbenos a <a href="mailto:orders@grupohaciendareal.com">orders@grupohaciendareal.com</a>.</P>
          </>
        ) : (
          <>
            <P>
              In compliance with Mexico's <strong>Federal Law on Protection of Personal Data Held by Private Parties (LFPDPPP)</strong>, <strong>Grupo Hacienda Real</strong> ("the Controller"), located in San Juan de los Lagos, Jalisco, Mexico, informs you of the following:
            </P>

            <H2>1. Personal data we collect</H2>
            <UL>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Delivery address (street, number, neighborhood, city, state, ZIP code, country)</li>
            </UL>
            <P>We do not collect financial data directly. Payments are processed by <strong>Stripe</strong>, which has its own privacy policies.</P>

            <H2>2. Purpose of processing</H2>
            <P>Your personal data is used solely to:</P>
            <UL>
              <li>Process and deliver your order</li>
              <li>Send you order confirmation and updates by email</li>
              <li>Handle inquiries or claims related to your purchase</li>
            </UL>
            <P>We do not use your data for advertising or marketing, nor share it with third parties other than the carriers required to deliver your order.</P>

            <H2>3. Data transfers</H2>
            <P>We only transfer your data to:</P>
            <UL>
              <li><strong>Shipping carriers</strong>: to generate the label and deliver your order (DHL, FedEx, Estafeta, UPS, or others)</li>
              <li><strong>Stripe</strong>: to securely process your payment</li>
            </UL>

            <H2>4. Your rights</H2>
            <P>
              You have the right to <strong>Access, Rectify, Cancel or Object (ARCO)</strong> to the processing of your personal data. To exercise these rights, send your request to{' '}
              <a href="mailto:orders@grupohaciendareal.com">orders@grupohaciendareal.com</a>{' '}
              including your full name and the right you wish to exercise. We will respond within 20 business days.
            </P>

            <H2>5. Cookies</H2>
            <P>Our website may use technical cookies required for its operation. We do not use advertising tracking cookies.</P>

            <H2>6. Changes to this notice</H2>
            <P>Any changes to this notice will be published on this page. We recommend reviewing it periodically.</P>

            <H2>7. Contact</H2>
            <P>For questions about the processing of your personal data, email us at <a href="mailto:orders@grupohaciendareal.com">orders@grupohaciendareal.com</a>.</P>
          </>
        )}

        <Divider />
        <P style={{fontSize:'.78rem', opacity:.5, textAlign:'center'}}>© {new Date().getFullYear()} Hacienda Real · Cajeta Artesanal</P>
      </Wrap>
    </Page>
    </>
  );
}
